import { AppstoreOutlined, ClockCircleOutlined, EditOutlined, EyeOutlined, FolderOpenOutlined, TableOutlined, ToTopOutlined } from "@ant-design/icons";
import { Button as Btn, Card, Col, Row, Tooltip } from "antd";
import microFinanceBig from "assets/svg/microFinanceBig.svg";
import VoucherCard from "components/Home/VoucherCard/VoucherCard";
import { useTranslation } from "contexts/TranslationContext";
import dayjs from "dayjs";
// Legacy hardcoded vendor list (kept here as a fallback — to revert, swap the V2 import below):
// import FeaturedProducts from "components/Home/FeaturedProducts/FeaturedProducts";
import FeaturedProductsV2 from "components/Home/FeaturedProductsV2/FeaturedProductsV2";
import OtherServices from "components/Home/OtherServices/OtherServices";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetPersonalInformationMutation, useGetUserAppsQuery, useUpdatePrivacyConsentMutation } from "services/auth";
import PrivacyModal from "components/PrivacyModal/PrivacyModal";
import useAuthHook from "hooks/useAuthHook";
import { useGetBusinessProviderQuery } from "services/lookup";
import { Button } from "shared/components/Button";
import env from "shared/constants/env";
import * as LOCAL_STORAGE_KEYS from "shared/constants/localStorageKeys";
import routes, { appRoute } from "shared/constants/routes";
import { getApiEndpoint } from "utilities/apiEndpoint";
// import RequestsFullCardsView from "../../components/Home/RequestsFullCardsView/RequestsFullCardsView";
import RequestsCardsNew from "../../components/Home/RequestsCardsNew/RequestsCardsNew";
import NoRequests from "assets/svg/noRequests.svg"
import RequestsFullCardsView from "../../components/Home/RequestsFullCardsView/RequestsFullCardsView";
import autoLeasing from "assets/svg/autoLeasing.jpg";
import MotoFinanceHome from "assets/svg/motoFinanceHome.jpg";
import personalFinance from "assets/svg/personalFinance.jpg";
import microFinance from "assets/svg/microFinanceBig.jpg";
import homeBlob from "assets/svg/homeBlob.svg";

// Alias so the existing JSX below (`<FeaturedProducts data={data} />`) stays unchanged.
// To revert: comment the V2 import above, uncomment the legacy import, delete this alias line.
const FeaturedProducts = FeaturedProductsV2;

const getStatusLabel = (statusId) => {
	switch (statusId) {
		case "D":
			return "Draft";
		case "S":
			return "Submitted";
		default:
			return "-";
	}
};

const RequestsViewToggleButton = (props) => {
	const isGrid = useCallback(() => props?.state === "grid", [props?.state]);

	return <Button onClick={props?.toggleRequestsView} icon={!isGrid() ? <AppstoreOutlined /> : <TableOutlined />}></Button>;
};

const RequestsGridCard = (props) => {
	const data = props?.data || {};
	const navigate = useNavigate();
	const { translate } = useTranslation();
	const isDraft = useCallback(() => data?.status?.toLowerCase() === "draft", [data?.status]);

	return (
		<Card
			title={`${translate("HOME.APPL_NO")}: ${data?.APPLID}`}
			extra={<Tooltip title={isDraft() ? "Draft" : "Ready"}>{isDraft() ? <ClockCircleOutlined /> : <ToTopOutlined />}</Tooltip>}
			actions={[
				<Button
					icon={isDraft() ? <EditOutlined /> : <EyeOutlined />}
					onClick={() => {
						//view Application
						if (data.APPLICATION_STATUS === "S") {
							navigate(`/app/${routes.VIEW_REQUEST}/${data.APPLID}`);
						}
					}}
					type='text'>
					{isDraft() ? translate("HOME.COMPLETE") : translate("HOME.VIEW")}
				</Button>,
			]}>
			<Row gutter={[8, 8]}>
				<Col span={24}>
					{translate("HOME.PRODUCT")}: <strong>{data?.PRODUCT_NAME ? `${data?.PRODUCT_NAME} / ${data?.PRODUCT_NAME_AR}` : "-"}</strong>
				</Col>
				<Col span={24}>
					{translate("HOME.CONTRACT")}: <strong>{data?.CONTRACT_STATUS || "-"}</strong>
				</Col>
				<Col span={24}>
					{translate("HOME.STATUS")}: <strong className={isDraft() ? "text-yellow-500" : "text-green-600"}>{data?.status || "-"}</strong>
				</Col>
			</Row>
		</Card>
	);
};

const RequestsGridView = (props) => {
	const requests = props?.requests || [];

	if (Array.isArray(props?.requests) && props?.requests.length === 0) {
		return (
			<div className='mt-6 text-gray-300 text-center flex flex-col gap-2'>
				<div>
					<FolderOpenOutlined style={{ fontSize: 32 }} />
				</div>
				<em>No data</em>
			</div>
		);
	}

	return (
		<Row gutter={[16, 16]}>
			{requests.map((request, i) => (
				<Col key={i} sm={24} lg={8}>
					<RequestsGridCard data={request} />
				</Col>
			))}
		</Row>
	);
};

const AutoLeaseAndMotoProducts = ({ data }) => {
	const { translate, language } = useTranslation();
	const navigate = useNavigate();
	const [user, setUser] = useState(null);
	const idStartsWithOne = user?.USER_ID?.toString().startsWith('1');

	useEffect(() => {
		// Fetch the user data from localStorage when component mounts
		const storedUser = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
		setUser(storedUser);
	}, []);


	const products = [
		{
			id: 1,
			title: translate("HOME.AUTO"),
			description: "HOME.AUTO_DESC",
			image: autoLeasing,
			route: 1,
			endpoint: env.API_ENDPOINT_V2
		},
		{
			id: 4,
			title: translate("HOME.MOTO"),
			description: "HOME.MOTO_DESC",
			image: MotoFinanceHome,
			route: 4,
			endpoint: env.API_ENDPOINT_V2
		},
	];

	const handleApplyNow = async (product) => {
		if ((data?.length >= 20 && product.route === 1)) {
			//for testing purposes, will be changed to 1
			toast.error(translate("APPLICATION_ALREADY_EXISTS"));
			return;
		}	
		else if (data?.length >= 5 && product.route === 4) {
			toast.error(translate("APPLICATION_ALREADY_EXISTS"));
			return;
		}
		try {
			const preAppStatus = await fetch(`${product.endpoint}app/pre-app-status?id=${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID}`)

			const response = await preAppStatus.json()

			// console.log('pre', response)

			if (response?.result?.completedSteps.length >= 4 && response?.result?.status === 'COMPLETED') {
				navigate(appRoute(routes.PRODUCTS + `/${product.route}`));
			} else {
				navigate(appRoute(routes.PRE_APP) + `?version=2`);
				toast.error("Please complete pre-application steps and try again.");
			}
		} catch (error) {
			console.error("Pre-App Status error:", error);
			toast.error("Something went wrong in checking pre app status. Please try again.");
		}
	};

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
			{products.map((product) => (
				<div
					key={product.id}
					className="relative rounded-3xl overflow-hidden shadow-md bg-white flex flex-col h-full"
				>
				<div className="relative w-full h-56 md:h-64">
					<img src={product.image} alt={product.id} className="w-full h-full object-cover" />
					<img
						src={homeBlob}
						alt="decorative blob"
						className={`absolute bottom-0 md:w-3/2 ${language.code === "ar" ? "right-0 scale-x-[-1] xl:w-1/2 " : "left-0 w-auto"}`}
					/>
					<div className={`absolute bottom-3 md:bottom-4 text-white ${language.code === "ar" ? "right-10 md:right-12" : "left-10 md:left-12"}`}>
							<h3 className="text-lg md:text-2xl font-medium mb-2">
								{product.title}
							</h3>
							<Btn
								className="rounded-full bg-white text-primary font-semibold px-4 py-1 text-xs md:text-sm border-0"
								onClick={() => handleApplyNow(product)}
							>
								{translate("HOME.CREATE_NEW")}
							</Btn>
						</div>
					</div>
				</div>
			))}
		</div>
	);
};

const MicroFinanceProduct = ({ data }) => {
	const { translate, language } = useTranslation();
	const navigate = useNavigate();
	const [user, setUser] = useState(null);

	const product =
	{
		id: 3,
		route: 3,
		endpoint: env.API_ENDPOINT_V2,
		version: 1
	}

	useEffect(() => {
		// Fetch the user data from localStorage when component mounts
		const storedUser = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
		setUser(storedUser);
	}, []);

	const handleApplyNow = async (product) => {
		if (data?.length >= 5
			//  && Array.isArray(data)
			//  && data.some(item => item.CONTRACT_STATUS !== 'SIGNED')
		) {
			toast.error(translate("APPLICATION_ALREADY_EXISTS"));
			return;
		}

		try {
			const preAppStatus = await fetch(`${product.endpoint}app/pre-app-status?id=${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID}`)
			const response = await preAppStatus.json()

			if (response?.result?.completedSteps.length >= 4 && response?.result?.status === 'COMPLETED') {
				navigate(appRoute(routes.PRODUCTS + `/${product.route}`))
			} else {
				navigate(appRoute(routes.PRE_APP) + `?version=${product.version}`);
				toast.error("Please complete pre-application steps and try again.");
			}
		} catch (error) {
			console.error("Pre-App Status error:", error);
			toast.error("Something went wrong in checking pre app status. Please try again.");
		}
	};

	return (
		<div className="relative">
			<div className="bg-[#E6F1F2] rounded-lg overflow-hidden flex flex-row">
				<div className="p-6 flex flex-col justify-center flex-1">
					<div>
						<h3 className="text-[#1F225B] md:text-3xl lg:text-4xl font-light mb-2">{translate("HOME.MICRO_PRODUCTS_TITLE")}</h3>
						<p className="text-[#8E9196] text-base mb-4">{translate("HOME.MICRO_DESC")}</p>
					</div>
					<Btn
						className="rounded-full bg-primary text-white font-semibold py-4 px-5 w-fit"
						onClick={() => handleApplyNow(product)} //3 for microfinance
					>
						{translate("HOME.CREATE_NEW")}
					</Btn>
				</div>
				<div className="w-full lg:w-2/3 md:w-2/5">
					<img
						className="w-full h-full object-cover"
						style={{
							clipPath: "polygon(0px 0px, 54% 0px, 86% 100%, 0px 100%)",
							transform: language.code === "ar" ? "" : "scaleX(-1)"
						}}
						src={microFinanceBig} alt="micro-banner"
					/>
				</div>
			</div>
		</div>
	);
};

const PersonalAndMicroProducts = ({ data }) => {
	const { translate, language } = useTranslation();
	const navigate = useNavigate();
	const [isSaudiUser, setIsSaudiUser] = useState(null);

	useEffect(() => {
		const storedUser = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
		const isSaudiUser = storedUser?.USER_ID?.toString().startsWith('1');
		setIsSaudiUser(isSaudiUser);
		console.log('isSaudiUser', isSaudiUser);
	}, []);

	const products = [
		{
			id: 2,
			title: translate("HOME.PERSONAL"),
			description: "HOME.PERSONAL_DESC",
			image: personalFinance,
			route: 2
		},
		{
			id: 3,
			title: translate("HOME.MICRO"),
			description: "HOME.MICRO_DESC",
			image: microFinance,
			route: 3,
			subDescription: "PRODUCTS.MICRO_DESC_SUB",
		}
	];

	const handleApplyNow = async (route) => {
		if (data?.length >= 50
			//  && Array.isArray(data) 
			//  && data.some(item => item.CONTRACT_STATUS !== 'SIGNED')
		) {
			toast.error(translate("APPLICATION_ALREADY_EXISTS"));
			return;
		}

		try {
			// School and Dental use v1 pre-steps
			const preAppStatus = await fetch(`${env.API_ENDPOINT_V2}app/pre-app-status?id=${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID}`);
			const response = await preAppStatus.json();

			if (response?.result?.completedSteps.length >= 4 && response?.result?.status === 'COMPLETED') {
				navigate(appRoute(routes.PRODUCTS + `/${route}`));
			} else {
				navigate(appRoute(routes.PRE_APP) + `?version=2`);
				toast.error("Please complete pre-application steps and try again.");
			}
		} catch (error) {
			console.error("Pre-App Status error:", error);
			toast.error("Something went wrong in checking pre app status. Please try again.");
		}
	};

	// Unified grid across breakpoints; stacks one per row on mobile
	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
			{products.map((product) => (
				<div
					key={product.id}
					className="relative rounded-3xl overflow-hidden shadow-md bg-white flex flex-col h-full"
				>
				<div className="relative w-full h-56 md:h-64">
					<img src={product.image} alt={product.id} className="w-full h-full object-cover" />
					<img
						src={homeBlob}
						alt="decorative blob"
						className={`absolute bottom-0 md:w-3/2 ${language.code === "ar" ? "right-0 scale-x-[-1] xl:w-1/2" : "left-0 w-auto"}`}
					/>
					<div className={`absolute bottom-3 md:bottom-4 text-white ${language.code === "ar" ? "right-10 md:right-12" : "left-10 md:left-12"}`}>
							<h3 className="text-lg md:text-2xl font-medium mb-2">
								{product.title}
							</h3>
							<Btn
								disabled={!isSaudiUser && product.id === 3}
								className={`rounded-full font-semibold px-4 py-1 text-xs md:text-sm border-0 ${!isSaudiUser && product.id === 3 ? "cursor-not-allowed text-gray-400 bg-slate-500" : "bg-white text-primary"}`}
								onClick={() => handleApplyNow(product.route)}
							>
								{translate("HOME.CREATE_NEW")}
							</Btn>
						</div>
					</div>
				</div>
			))}
		</div>
	);
};

export default function Home() {
	const [user, setUser] = useState({});
	const [privacyConsentOpen, setPrivacyConsentOpen] = useState(false);

	const { logout } = useAuthHook();
	const [updatePrivacyConsent, { isLoading: isAcceptingPrivacy }] = useUpdatePrivacyConsentMutation();

	let isSaudiUser = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.USER_ID?.toString().startsWith('1');
	// const [view, setView] = useState("fullCard");
	const { data, isLoading, refetch } = useGetUserAppsQuery(JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID);

	const [fetchPersonalInfo, { isLoading: personalInfoLoading }] = useGetPersonalInformationMutation();

	const { data: businessProviders } =
		useGetBusinessProviderQuery();

	const { translate, language } = useTranslation();

	const columnsV1 = [
		{
			title: translate("OFFER_TABLE.APP_NO"),
			dataIndex: "APPLID",
			key: "APPLID",
			sorter: () => true,
		},
		{
			title: translate("OFFER_TABLE.PRODUCT_NAME"),
			dataIndex: "productName",
			key: "productName",
			sorter: () => true,
		},
		{
			title: translate("OFFER_TABLE.CONTRACT"),
			dataIndex: "contractStatus",
			key: "contractStatus",
			sorter: () => true,
		},
		{
			title: translate("OFFER_TABLE.STATUS"),
			dataIndex: "status",
			key: "status",
			sorter: () => true,
		},
	];

	// const actionsv1 = [
	// 	{
	// 		id: 0,
	// 		icon: FileTextOutlined,
	// 		label: translate("OFFER_TABLE.CONTRACT"),
	// 		actionKey: "Contract",
	// 	},
	// 	{
	// 		id: 1,
	// 		icon: TagsOutlined,
	// 		label: translate("OFFER_TABLE.OFFER"),
	// 		actionKey: "Offer",
	// 	},
	// 	// {
	// 	// 	id: 2,
	// 	// 	icon: ReloadOutlined,
	// 	// 	label: translate("OFFER_TABLE.REFRESH"),
	// 	// 	actionKey: "refresh",
	// 	// },
	// 	{
	// 		id: 2,
	// 		icon: DeleteOutlined,
	// 		label: translate("OFFER_TABLE.DELETE"),
	// 		actionKey: "Delete",
	// 	},
	// 	// {
	// 	// 	id: 4,
	// 	// 	icon: FileTextOutlined,
	// 	// 	label: translate("OFFER_TABLE.VIEW_CONTRACT"),
	// 	// 	actionKey: "View Contract",
	// 	// },
	// 	{
	// 		id: 3,
	// 		icon: EditOutlined,
	// 		label: translate("OFFER_TABLE.EDIT"),
	// 		actionKey: "Edit",
	// 	},
	// ];

	// const toggleRequestsView = useCallback(() => {
	// 	setView((view) => (view === "grid" ? "table" : "grid"));
	// }, []);

	const navigate = useNavigate();

	let tableData = data?.map((row) => ({
		...row,
		productName: row?.PRODUCT_NAME ? `${row?.PRODUCT_NAME} / ${row?.PRODUCT_NAME_AR}` : "-",
		contractStatus: row?.CONTRACT_STATUS ? row?.CONTRACT_STATUS : "-",
		status: getStatusLabel(row?.APPLICATION_STATUS),
	}));

	// Cache application source information for API routing
	useEffect(() => {
		if (data && data.length > 0) {
			try {
				// Create a map of application IDs to their API source
				const appSourceMap = data.reduce((acc, app) => {
					if (app.APPLID && app.__apiSource) {
						acc[app.APPLID] = app.__apiSource;
					}
					return acc;
				}, {});

				// Store in localStorage for use by API service
				localStorage.setItem('userAppsSourceMap', JSON.stringify(appSourceMap));
			} catch (error) {
				console.error('Error caching application source information:', error);
			}
		}
	}, [data]);

	useEffect(() => {
		let user;

		try {
			user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER));

			user.APP_CREATED = false;

			localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));
			setUser(user);
		} catch (e) {
			console.error(e);
		}
	}, [data]);

	useEffect(() => {
		setUser(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER)));

		refetch();
	}, []);

	// Privacy consent gate — check once on mount
	useEffect(() => {
		try {
			const storedUser = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER));
			if (storedUser && storedUser.PRIVACY_ENABLED !== 1) {
				setPrivacyConsentOpen(true);
			}
		} catch (e) {
			console.error('Privacy consent check failed:', e);
		}
	}, []);

	const handlePrivacyAccept = async () => {
		try {
			const storedUser = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER));
			await updatePrivacyConsent({ id: storedUser.IDB_USR_ID }).unwrap();
			const updatedUser = { ...storedUser, PRIVACY_ENABLED: 1 };
			localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(updatedUser));
			setPrivacyConsentOpen(false);
		} catch (e) {
			console.error('Failed to update privacy consent:', e);
			toast.error('Something went wrong. Please try again.');
		}
	};

	const handlePrivacyDecline = () => {
		logout();
	};

	const onDelete = (appId) => {
		fetch(env.API_ENDPOINT + `app/delete-app?appId=${appId}`, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
			},
		})
			.then((res) => res.json())
			.then((res) => {
				if (res?.result?.deleted) {
					toast.success("App has been deleted.");
					refetch();
				} else {
					toast.error("Server Error");
				}
			});
	};
	const onEdit = (applId) => {
		navigate(appRoute(routes.CREATE_REQUEST + "/" + routes.CREATE_REQUEST_LOAN_DETAILS) + `?appl_=${applId}`);
	}

	const handleCreateNewApplication = async () => {
		if (data?.length > 0 && Array.isArray(data) && data.some(item => item.CONTRACT_STATUS !== 'SIGNED')) {
			toast.error(translate("APPLICATION_ALREADY_EXISTS"));
			return;
		}

		try {
			const preAppStatus = await fetch(`${getApiEndpoint()}app/pre-app-status?id=${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID}`)

			const response = await preAppStatus.json()

			// console.log('pre', response)

			if (response?.result?.completedSteps.length >= 4 && response?.result?.status === 'COMPLETED') {
				navigate(appRoute(routes.CREATE_REQUEST + "/" + routes.CREATE_REQUEST_LOAN_DETAILS));
			} else {
				navigate(appRoute(routes.PRE_APP));
				toast.error("Please complete pre-application steps and try again.");
			}
		} catch (error) {
			console.error("Pre-App Status error:", error);
			toast.error("Something went wrong in checking pre app status. Please try again.");
		}
	};

	// if (data) {
	// 	try {
	// 		const response = await fetchPersonalInfo({ id: `${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.USER_ID}` }).unwrap().catch((res) => {
	// 			// console.log("Personal info err resp:", res);
	// 		});

	// 		// route to load-details with appl id and dispatch
	// 		if (response?.payload?.data?.address?.personNationalAddressInfo?.Addresses) {
	// 			navigate(appRoute(routes.CREATE_REQUEST + "/" + routes.CREATE_REQUEST_LOAN_DETAILS));
	// 		}
	// 		else {
	// 			toast.error(response?.payload?.data?.address?.error?.message || 'Please update your national address and try again');
	// 		}
	// 	} catch (error) {
	// 		//for now this will be error for personalInfo
	// 		// console.log("Personal info resp er:", error);
	// 		return toast.error(translate("Something went wrong"));
	// 	}
	// } else {
	// 	return toast.error(translate("Something went wrong"));
	// }


	const noRequests = () => {
		if (!tableData || tableData.length === 0) {
			return (
				<div className="flex flex-col items-center justify-center mt-10 gap-4">
					<img className="w-1/3 h-1/3" src={NoRequests} />
					<p className="text-center text-xl sm:text-2xl font-medium">{translate('HOME.NO_REQUESTS')} </p>
				</div>
			)
		}
	}

	return (
		<>
			<PrivacyModal
				consentMode
				open={privacyConsentOpen}
				onAccept={handlePrivacyAccept}
				onDecline={handlePrivacyDecline}
				isAccepting={isAcceptingPrivacy}
			/>
			{/* Bottom-left image */}
			{/* <img
				src={BgBottomLeftIcon}
				alt="Bottom Left Decoration"
				className="absolute bottom-0 w-2/5 md:w-2/5 lg:w-2/5 -z-10"
			  /> */}
			{/* Header Section */}
			<div className="relative">
				<div className="container mx-auto md:py-12 px-4">
					{/* Mobile Header */}
					<div className="block md:hidden bg-[#F8F9FA] rounded-xl p-4 mb-6">
						<h1 className="text-xl font-normal text-[#16151C] mb-1">
							{translate("HOME.GREETINGS")}, <span className="text-primary font-medium">{user?.FIRST_NAME}</span>
						</h1>
						{user?.LAST_LOGIN && (
							<div className="flex items-center gap-2 text-sm">
								<div className="text-primary ">
								<ClockCircleOutlined />
								</div>
								<span className="text-[#56667C]">{translate("HOME.LAST_LOGIN").toLowerCase()}: {dayjs(user?.LAST_LOGIN).format("dddd, D MMM")}</span>
							</div>
						)}
					</div>
					{/* Desktop Header */}
					<div className="hidden md:block">
						<h1 className="text-3xl text-[#101010] font-bold mb-2">
							{translate("HOME.Hi")},{" "}
							<strong>{user?.FIRST_NAME}</strong>!
						</h1>
						{user?.LAST_LOGIN && (
							<div className="text-[#878787] text-lg mb-6">
								{translate("HOME.LAST_LOGIN")}:{" "}
								<strong>{dayjs(user?.LAST_LOGIN).format("dddd, D MMM ")}</strong>
							</div>
						)}
					</div>
				</div>
			</div >

			<div className="container mx-auto pb-10 px-4" >
				<PersonalAndMicroProducts data={data} />
			</div>

			<div className="container mx-auto pb-10 px-4" >
				<AutoLeaseAndMotoProducts data={data} />
			</div>

			{/* {isSaudiUser &&  //micro only available to saudi users
				<div>
					<div className="container mx-auto px-4">
						<h1 className="text-3xl md:text-5xl text-[#5F5F5F] mt-12 font-extralight mb-12">
							{translate("HOME.MICRO_SUB")}
						</h1>
					</div>
					<div className="container mx-auto pb-10 px-4" >
						<MicroFinanceProduct data={data} />
					</div>
				</div>
			} */}


			{/* Feature Products Section */}
			<div className="container mx-auto px-4">
				<h1 className="text-3xl md:text-5xl text-[#212044] mt-12 font-semibold mb-8 text-center">
					{translate("HOME.FEATURE_PRODUCTS")}
				</h1>
			</div>
			<div className="container mx-auto pb-10 px-4">
				<FeaturedProducts data={data} />
			</div>

			{/* Other Services Section — digital services carousel (Coming Soon on click) */}
			<div className="container mx-auto pb-10 px-4">
				<OtherServices />
			</div>


			{/* Services Section */}
			{/* <div className="container mx-auto px-4">
				<h1 className="text-3xl md:text-5xl text-[#16151C] mt-12 font-extralight mb-6">
					{translate("HOME.CARDS_TITLE")}
				</h1>
			</div>
			< div className="container mx-auto pb-10 px-4" >
				<VoucherCard data={data} />
			</div > */}


			{/* Request Section */}
			<section>
				<div className={`p-6 ${(!tableData || tableData.length == 0) && 'shadow-lg bg-white rounded-lg border border-gray-100'}`}>
					{(!tableData || tableData.length == 0) && <div className="flex items-center justify-between mb-4">
						<h2 className="text-2xl sm:text-3xl font-semibold text-[#212044]">
							{translate("HOME.REQUEST")}
						</h2>
					</div>}

					{<RequestsCardsNew onEdit={onEdit} onDelete={onDelete} requests={tableData} />}
					{!isLoading && noRequests()}
				</div>
			</section >
		</>
	);
}

